import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Agent, api, Channel, Dm, PublicUser, SearchResults, SearchFilters } from './api';
import { DateTimePicker } from './DateTimePicker';
import { avatarStyle } from './AccountMenu';
import { Icon } from './icons';
import { AgentFace } from './AgentAvatar';

export type JumpAction =
  | { kind: 'open'; id: string }
  | { kind: 'dm'; memberType: 'user' | 'agent'; id: string };

type Row = {
  key: string;
  group: string;
  icon: React.ReactNode;
  avStyle?: React.CSSProperties;
  primary: React.ReactNode;
  secondary?: string;
  action: JumpAction;
};

const EMPTY: SearchResults = { messages: [], files: [], people: [], channels: [], kb: [], semantic: [] };

type BoolFilterKey = 'tagged_me' | 'has_links' | 'has_reactions';
const FILTER_TOGGLES: [BoolFilterKey, string][] = [
  ['tagged_me', '@ Tagged me'],
  ['has_links', '🔗 Has link'],
  ['has_reactions', '😀 Reacted'],
];
const FILTBAR: React.CSSProperties = {
  display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center',
  padding: '8px 14px', borderBottom: '1px solid rgba(128,128,128,0.18)',
};
const FCHIP: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 8,
  border: '1px solid rgba(128,128,128,0.32)', cursor: 'pointer', fontFamily: 'inherit',
};
const FDATE: React.CSSProperties = {
  fontSize: 12, padding: '3px 6px', borderRadius: 6, border: '1px solid rgba(128,128,128,0.32)',
  background: 'transparent', color: 'inherit', fontFamily: 'inherit',
};

/** Spotlight command palette: global search over channels, DMs, people, messages, files. */
export function CommandPalette({
  workspaceId,
  channels,
  dms,
  members,
  agents,
  onJump,
  onClose,
}: {
  workspaceId: string;
  channels: Channel[];
  dms: Dm[];
  members: PublicUser[];
  agents: Agent[];
  onJump: (a: JumpAction) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({});
  const [server, setServer] = useState<SearchResults>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const hasFilters = !!(
    filters.after || filters.before || filters.sender || filters.channel
    || filters.has_links || filters.has_reactions || filters.tagged_me || filters.file_type
  );

  // Server search (messages + files) — debounced; runs on text OR any active filter, so a
  // filter-only query ("everything I was tagged in this week") works. Channels/DMs/people are
  // matched locally.
  useEffect(() => {
    const term = q.trim();
    if (!term && !hasFilters) {
      setServer(EMPTY);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const t = window.setTimeout(() => {
      api.search(workspaceId, term, filters)
        .then(setServer)
        .catch(() => setServer(EMPTY))
        .finally(() => setLoading(false));
    }, 190);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, workspaceId, filters, hasFilters]);

  const term = q.trim().toLowerCase();
  const has = (s?: string | null) => !term || (s || '').toLowerCase().includes(term);

  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    const initial = (s?: string) => (s?.trim()?.[0] || '?').toUpperCase();

    const localChannels = (term ? channels.filter((c) => has(c.name)) : channels).slice(0, 6);
    const localDms = (term ? dms.filter((d) => has(d.name)) : dms).slice(0, 6);

    localChannels.forEach((c) =>
      out.push({
        key: `c-${c.id}`, group: 'Channels',
        icon: <Icon name={c.kind === 'private' ? 'lock' : 'hash'} size={15} />,
        primary: c.name, secondary: c.topic || (c.kind === 'private' ? 'Private channel' : 'Channel'),
        action: { kind: 'open', id: c.id },
      }),
    );
    localDms.forEach((d) =>
      out.push({
        key: `d-${d.id}`, group: 'Direct messages',
        icon: d.dm_with.type === 'agent' ? <AgentFace name={d.name} avatarUrl={d.dm_with.avatar_url} size={22} /> : initial(d.name),
        ...(d.dm_with.type === 'agent' ? { avStyle: { backgroundColor: 'transparent' } as React.CSSProperties } : {}),
        avStyle: d.dm_with.avatar_url ? { backgroundImage: `url(${d.dm_with.avatar_url})` } : undefined,
        primary: d.name, secondary: 'Direct message',
        action: { kind: 'open', id: d.id },
      }),
    );

    if (term) {
      members.filter((m) => has(m.name) || has(m.email)).slice(0, 6).forEach((m) =>
        out.push({
          key: `p-${m.id}`, group: 'People',
          icon: initial(m.name || m.email), avStyle: avatarStyle(m),
          primary: m.name || m.email, secondary: m.role ? `${m.role}` : m.email,
          action: { kind: 'dm', memberType: 'user', id: m.id },
        }),
      );
      agents.filter((a) => has(a.name)).slice(0, 6).forEach((a) =>
        out.push({
          key: `a-${a.id}`, group: 'People',
          icon: <AgentFace name={a.name} avatarUrl={a.avatar_url} size={22} />, avStyle: { backgroundColor: 'transparent' } as React.CSSProperties, primary: a.name, secondary: 'Agent',
          action: { kind: 'dm', memberType: 'agent', id: a.id },
        }),
      );
      server.messages.forEach((m) =>
        out.push({
          key: `m-${m.message_id}`, group: 'Messages',
          icon: <Icon name={m.channel_kind === 'dm' ? 'dm' : 'hash'} size={15} />,
          primary: (
            <>
              <b>{m.sender_name}</b>: {m.body}
            </>
          ),
          secondary: `${m.channel_kind === 'dm' ? '' : '#'}${m.channel_name || 'conversation'}${
            m.created_at ? ` · ${new Date(m.created_at).toLocaleDateString()}` : ''
          }`,
          action: { kind: 'open', id: m.channel_id },
        }),
      );
      server.files.forEach((f) =>
        out.push({
          key: `f-${f.id}`, group: 'Files',
          icon: '📎', primary: f.name,
          secondary: `${f.channel_name ? `#${f.channel_name}` : ''}${
            f.size ? ` · ${Math.max(1, Math.round(f.size / 1024))} KB` : ''
          }`,
          action: { kind: 'open', id: f.channel_id },
        }),
      );
      (server.kb || []).forEach((k) =>
        out.push({
          key: `kb-${k.id}`, group: 'Knowledge',
          icon: '📚', primary: k.title,
          secondary: `${(k.body || '').replace(/\s+/g, ' ').slice(0, 90)}${k.status === 'draft' ? ' · draft' : ''}`,
          action: { kind: 'open', id: k.source_channel_id || '' },
        }),
      );
      (server.semantic || []).forEach((m) =>
        out.push({
          key: `sm-${m.id}`, group: 'Related by meaning',
          icon: '✨',
          primary: (
            <>
              <b>{m.sender_name || 'Someone'}</b>: {m.body.slice(0, 120)}
            </>
          ),
          secondary: `${Math.round((m.similarity || 0) * 100)}% match — no keyword overlap needed`,
          action: { kind: 'open', id: m.channel_id },
        }),
      );
    }
    return out;
  }, [term, channels, dms, members, agents, server]);

  useEffect(() => { setActive(0); }, [rows.length]);

  useEffect(() => {
    listRef.current?.querySelector('.fx-cmd-row.is-active')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const r = rows[active]; if (r) { onJump(r.action); onClose(); } }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  // group headers rendered inline as the group changes
  let lastGroup = '';

  return (
    <div className="fx-cmd-backdrop" onClick={onClose}>
      <div className="fx-cmd" onClick={(e) => e.stopPropagation()} onKeyDown={onKey}>
        <div className="fx-cmd-inputwrap">
          <Icon name="search" size={18} />
          <input
            ref={inputRef}
            className="fx-cmd-input"
            placeholder="Search messages, files, people and channels…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {loading && <span className="fx-cmd-spin" />}
        </div>

        <div style={FILTBAR}>
          {(FILTER_TOGGLES).map(([key, label]) => {
            const on = !!filters[key];
            return (
              <button key={key} type="button" onClick={() => setFilters((f) => ({ ...f, [key]: !f[key] }))}
                style={{ ...FCHIP, background: on ? 'var(--fx-accent, #f7b500)' : 'transparent',
                  borderColor: on ? 'var(--fx-accent, #f7b500)' : 'rgba(128,128,128,0.32)',
                  color: on ? '#1a1200' : 'inherit' }}>
                {label}
              </button>
            );
          })}
          <DateTimePicker mode="date" ariaLabel="On or after" placeholder="On or after" value={filters.after || ''} style={{ minWidth: 132 }}
            onChange={(v) => setFilters((f) => ({ ...f, after: v || undefined }))} />
          <span style={{ color: 'var(--fx-muted, #9092a0)', fontSize: 12 }}>→</span>
          <DateTimePicker mode="date" ariaLabel="On or before" placeholder="On or before" value={filters.before || ''} style={{ minWidth: 132 }}
            onChange={(v) => setFilters((f) => ({ ...f, before: v || undefined }))} />
          {hasFilters && (
            <button type="button" onClick={() => setFilters({})}
              style={{ fontSize: 12, color: 'var(--fx-muted, #9092a0)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
              Clear
            </button>
          )}
        </div>

        <div className="fx-cmd-list" ref={listRef}>
          {rows.length === 0 && (
            <div className="fx-cmd-empty">
              {term || hasFilters ? 'No matches.' : 'Type to search — or add a filter above.'}
            </div>
          )}
          {rows.map((r, i) => {
            const header = r.group !== lastGroup ? r.group : null;
            lastGroup = r.group;
            return (
              <React.Fragment key={r.key}>
                {header && <div className="fx-cmd-group">{header}</div>}
                <button
                  type="button"
                  className={`fx-cmd-row${i === active ? ' is-active' : ''}`}
                  onMouseMove={() => setActive(i)}
                  onClick={() => { onJump(r.action); onClose(); }}
                >
                  <span className="fx-cmd-ic" style={r.avStyle}>{r.icon}</span>
                  <span className="fx-cmd-main">
                    <span className="fx-cmd-primary">{r.primary}</span>
                    {r.secondary && <span className="fx-cmd-secondary">{r.secondary}</span>}
                  </span>
                </button>
              </React.Fragment>
            );
          })}
        </div>

        <div className="fx-cmd-foot">
          <span><span className="fx-kbd">↑</span><span className="fx-kbd">↓</span> move</span>
          <span><span className="fx-kbd">↵</span> jump</span>
          <span><span className="fx-kbd">esc</span> close</span>
        </div>
      </div>
    </div>
  );
}
